import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, ArrowLeft, Search } from "lucide-react";
import PublicLayout from "./PublicLayout";
import { mcpServerUrl, claudeCodeCommand } from "../service/OAuthService";

const categories = [
    { key: "general", label: "General" },
    { key: "scoring", label: "Scoring & Methodology" },
    { key: "features", label: "Features & Capabilities" },
    { key: "privacy", label: "Privacy & Data" },
    { key: "usage", label: "Usage & Guidance" },
];

const faqData = [
    // ── General ──────────────────────────────────────────
    {
        category: "general",
        question: "What is 42Degrees?",
        answer: "42Degrees is a research intelligence platform built by researchers for researchers. 42Degrees promotes a new model of science publishing that (1) delivers tools for research evaluation and positioning, (2) enables fair, timely reviews of research manuscripts, and (3) elevates clear, multimodal communication. 42Degrees is committed to open science that celebrates creativity and impact, and to sharing ideas in ways that drive discovery.",
    },
    {
        category: "general",
        question: "What is DeepThought?",
        answer: "DeepThought is a tool that embeds a written work into a heterogeneous knowledge graph built from thousands of scientific publications. Its structural position in this graph is used both to identify related research and potential collaborators, and to estimate the work\u2019s novelty, methodological rigor, and potential impact.",
    },
    {
        category: "general",
        question: "What types of papers can I upload?",
        answer: "DeepThought currently supports research articles, preprints, and manuscripts in microbiology and virology and closely related domains. The system is optimized for documents within these fields; papers outside these domains may receive less stable results. Efforts are underway to expand the network to additional areas.",
    },
    {
        category: "general",
        question: "Who designed the scoring system, and what are their qualifications?",
        answer: "A team of academic scientists, engineers, and an instructional designer created the scoring system as part of the 42Degrees initiative. This work draws on active research in computational biology, network theory, and AI\u2011driven scientific analysis. Please see our white paper for a list of contributors and affiliations.",
    },
    {
        category: "general",
        question: "Who is using DeepThought?",
        answer: "A small group of scientists and early adopters are currently testing the system. 42Degrees actively welcomes user feedback to refine both the models and the user experience.",
    },

    // ── Scoring & Methodology ────────────────────────────
    {
        category: "scoring",
        question: "How does the scoring system work?",
        answer: "Each uploaded manuscript is represented as a node in a large heterogeneous graph derived from tens of thousands of papers. The system computes how that node connects to other papers and concept nodes using graph\u2011theoretic features and learned graph embeddings. A set of machine\u2011learning models then maps these features to calibrated probabilities for Low, Medium, and High on three dimensions\u2014Novelty, Methods, and Impact\u2014and converts these into 0\u201399 ordinal scores with uncertainty estimates. These dimension scores can be summarized into an Overall Score. See our white paper for more details.",
    },
    {
        category: "scoring",
        question: "What do the Novelty, Methodology, and Impact scores mean?",
        answer: "Novelty reflects creative synthesis and rare structural connections in the graph\u2014papers that bridge otherwise weakly connected methods, problems, or impact clusters tend to score higher. Methods evaluates the rigor and transferability of the methodological approach, as inferred from the paper\u2019s position relative to authoritative methods and well\u2011validated techniques. Impact estimates how influential the work may become based on its centrality, bridging role, and connections to high\u2011value publications in the network. Together, these dimensions provide a holistic view of scientific contribution.",
    },
    {
        category: "scoring",
        question: "What does a Novelty score (e.g., 63) actually mean?",
        answer: "Each score represents a manuscript\u2019s relative position within the scientific landscape covered by the model. A higher Novelty score indicates stronger evidence of bridging previously disconnected areas or introducing rare concept combinations in that landscape. Exact percentile interpretations are not yet standardized; see our white paper for more details.",
    },
    {
        category: "scoring",
        question: "How is Impact estimated?",
        answer: "Impact scoring relies on graph\u2011theoretic measures of how centrally a paper sits in the network, how it connects to authoritative and high\u2011impact neighbors, and how it bridges research communities. During validation, the Impact model achieved about 90% AUC in distinguishing expert\u2011labeled high\u2011 from low\u2011impact publications.",
    },
    {
        category: "scoring",
        question: "How accurate are the scores?",
        answer: "The system was validated using stratified cross\u2011validation and bootstrap analysis on an expert\u2011labeled set. It achieved F1 scores of roughly 0.87 for Methods, 0.87 for Novelty, and 0.90 for Impact, with Impact AUC around 0.90 for distinguishing high\u2011 vs low\u2011impact publications. Confidence intervals on both performance and individual scores quantify uncertainty.",
    },
    {
        category: "scoring",
        question: "Have the scores been benchmarked against expert human evaluations?",
        answer: "Yes. Scores were compared with expert qualitative rankings of a collection of papers. The algorithm\u2019s highest\u2011 and lowest\u2011scoring outputs aligned closely with expert judgments, demonstrating strong agreement in identifying high\u2011 and low\u2011quality work within the evaluated set.",
    },
    {
        category: "scoring",
        question: "Is the scoring method transparent or explainable?",
        answer: "Partially. The system combines interpretable network metrics with learned components. For example, high within\u2011community centrality and authority scores tend to increase Impact and Methods, while strong bridging and structural\u2011hole features (disruptive potential, bridging score) tend to increase Novelty and Impact by connecting disparate areas. PageRank\u2011like prestige measures contribute positively to both Impact and perceived methodological authority.",
    },
    {
        category: "scoring",
        question: "What datasets were used for training the scoring system?",
        answer: "The training corpus comprises approximately 250,000 virology and microbiology publications (2021\u20132025), organized into semantic networks using transformer\u2011based embeddings. For initial calibration, 47 expert\u2011rated Articles of Interest were used to align the scoring behavior with human judgments on Methods, Novelty, and Impact.",
    },
    {
        category: "scoring",
        question: "How do you ensure scores aren\u2019t biased toward specific disciplines, languages, or writing styles?",
        answer: "Within microbiology and virology, the scoring system relies on semantic embeddings and network structure rather than raw citation counts, journal names, or surface keywords, which reduces\u2014but does not eliminate\u2014bias from popularity, English dominance, or stylistic variation. Areas or communities with limited training data or sparse network connections may yield less stable scores.",
    },
    {
        category: "scoring",
        question: "What are the known limitations of the scoring system?",
        answer: "The best performance requires larger, diverse expert\u2011rated training sets. Current models are early\u2011stage and trained on a small set of expert\u2011scored publications with class imbalance across Low/Medium/High categories. The system also relies primarily on recent microbiology and virology literature (roughly 2021\u20132025) and assumes that relationships between graph structure and quality remain reasonably stable over time, which may not hold in all subfields.",
    },

    // ── Features & Capabilities ──────────────────────────
    {
        category: "features",
        question: "Does the app provide feedback or suggestions in addition to scores?",
        answer: "Yes. Each score is accompanied by brief, structure\u2011aware suggestions that highlight where the manuscript appears strongest or weakest in terms of Novelty, Methods, and Impact, and how authors might improve framing or grounding based on what \u201cthe graph sees.\u201d",
    },
    {
        category: "features",
        question: "Does the system handle interdisciplinary or unconventional research?",
        answer: "Yes, to an extent. Interdisciplinary connections are explicitly rewarded: the Novelty score values papers that bridge distinct research areas or clusters in the graph. However, extremely niche or underrepresented fields may receive less stable scores due to limited training data and sparse network context.",
    },
    {
        category: "features",
        question: "Does the scoring system work for all scientific fields?",
        answer: "Not yet. The system is currently optimized for microbiology, virology, and closely related biomedical domains where the underlying semantic networks and training labels are strongest. Future expansions will broaden coverage to additional fields as new graphs and labels are developed.",
    },
    {
        category: "features",
        question: "Can I compare my score against other similar documents in the same field?",
        answer: "This feature is under development. The underlying models are capable of providing relative positioning within the scored corpus, and we plan to expose field\u2011specific comparison tools in future releases.",
    },
    {
        category: "features",
        question: "Can the app detect fabricated or fraudulent research?",
        answer: "No. DeepThought cannot verify whether results, data, or figures are genuine. It analyzes semantic and network\u2011level relationships, not factual accuracy or data integrity, so it cannot reliably detect fabricated or fraudulent findings.\n\nEvery manuscript does pass through an automated screening step that checks whether the file looks like a real scholarly document (e.g., filters out posters, abstracts\u2011only, or obvious junk) and flags potential AI\u2011generated or toxic content using a multi\u2011head classifier. These checks help ensure only plausible research articles enter the graph\u2011based scoring pipeline, but they are not fraud detectors and should never be used as a substitute for peer review, statistical checking, or human scrutiny.",
    },
    {
        category: "features",
        question: "Can I use DeepThought with Claude or other AI assistants?",
        answer: `Yes. DeepThought has an MCP server, so an AI assistant you connect to your account can read your papers, scores and written analysis, look up similar papers and DeepThinkers, and submit papers for scoring for you. It can\u2019t change, share or delete anything, and submissions count toward your daily limit.\n\nIn claude.ai or the Claude desktop app: open Settings \u2192 Connectors \u2192 Add custom connector, name it DeepThought, enter ${mcpServerUrl}, click Connect, then sign in to DeepThought and allow access.\n\nIn Claude Code: run ${claudeCodeCommand}, then run /mcp, choose deepthought and Authenticate.\n\nOther assistants that support remote MCP servers connect with the same URL. You can see and disconnect connected apps at any time in Settings \u2192 Privacy.`,
    },
    {
        category: "features",
        question: "How is related research identified?",
        answer: "At a high level, related research is identified by locating papers that are close to the focal manuscript in the semantic knowledge graph\u2014i.e., those sharing similar method, problem, or impact neighborhoods or occupying nearby positions in the learned embedding space.",
    },
    {
        category: "features",
        question: "How are percent similarities of related research papers calculated?",
        answer: "Percent similarity values can be derived from distances in the embedding space or edge\u2011based similarity measures in the graph (e.g., overlap in neighbors or cluster membership), normalized to a 0\u2013100 range.",
    },
    {
        category: "features",
        question: "How are DeepThinkers identified?",
        answer: "Similar to Related Research, DeepThinkers are the authors of papers that are close to the focal manuscript in the semantic knowledge graph\u2014i.e., papers that have similar methods, problems, or impact neighborhoods or that occupy nearby positions in the learned embedding space.",
    },

    // ── Privacy & Data ───────────────────────────────────
    {
        category: "privacy",
        question: "Will my document be used to train the scoring system?",
        answer: "No. Uploaded manuscripts are used only to generate scores and explanations for you. They are not added to the training datasets and are not shared with third parties.",
    },
    {
        category: "privacy",
        question: "Where does my document go after it is uploaded? Should I be worried about my ideas being stolen?",
        answer: "Manuscripts are processed privately and securely. You control whether your files remain stored for future reference. We do not share, sell, or publish manuscripts, and we do not expose your content to other users.",
    },
    {
        category: "privacy",
        question: "Can I delete my data?",
        answer: "Yes. You can delete any submission at any time using the red \u201cDelete\u201d button on a submission\u2019s scoring page.",
    },

    // ── Usage & Guidance ─────────────────────────────────
    {
        category: "usage",
        question: "How should scientists use these scores?",
        answer: "Scores are decision\u2011support tools, not verdicts. They help authors understand methodological strength, conceptual originality, and potential influence, but they do not replace expert evaluation, peer review, or domain\u2011specific judgment.",
    },
    {
        category: "usage",
        question: "Will these scores affect publication or funding decisions?",
        answer: "No. The system is experimental and intended for research analytics, manuscript development, and self\u2011assessment\u2014not for editorial or grantmaking decisions.",
    },
    {
        category: "usage",
        question: "Are there safeguards against over-reliance on automated metrics?",
        answer: "Yes. The DeepThought approach is explicitly positioned as a complement to, not a replacement for, human expertise. It intentionally contrasts with citation\u2011driven metrics that can reward popularity over intrinsic quality by focusing instead on structural signals of rigor, novelty, and impact, and by providing uncertainty estimates to discourage over\u2011interpretation of single numbers.",
    },
];

// FAQPage structured data built from the same questions, so search engines
// can read every answer even though the accordion renders one at a time.
const faqSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map(f => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
});

function FaqBody({ navigate }) {
    const [activeCategory, setActiveCategory] = useState("all");
    const [openFaq, setOpenFaq] = useState(null);
    const [search, setSearch] = useState("");

    const filtered = faqData.filter(f => {
        const matchesCategory = activeCategory === "all" || f.category === activeCategory;
        const matchesSearch = !search || f.question.toLowerCase().includes(search.toLowerCase()) || f.answer.toLowerCase().includes(search.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    return (
        <>
            <button className="faq-back-btn" onClick={() => navigate(-1)}>
                <ArrowLeft size={16} />
                <span>Back</span>
            </button>
            <h1 className="faq-title">Frequently Asked Questions</h1>
            <p className="faq-subtitle">
                Everything you need to know about DeepThought and the 42Degrees platform.
            </p>

            <div className="faq-search-wrap">
                <Search size={16} className="faq-search-icon" />
                <input
                    className="faq-search-input"
                    type="text"
                    placeholder="Search questions..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            <div className="faq-tabs" style={{ marginTop: 28 }}>
                <button
                    className={"faq-tab" + (activeCategory === "all" ? " faq-tab-active" : "")}
                    onClick={() => setActiveCategory("all")}
                >
                    All
                </button>
                {categories.map(cat => (
                    <button
                        key={cat.key}
                        className={"faq-tab" + (activeCategory === cat.key ? " faq-tab-active" : "")}
                        onClick={() => setActiveCategory(cat.key)}
                    >
                        {cat.label}
                    </button>
                ))}
            </div>

            <div className="faq-list">
                {filtered.length === 0 && (
                    <p className="faq-empty">No matching questions found.</p>
                )}
                {filtered.map((faq, i) => {
                    const key = `${faq.category}-${i}`;
                    const isOpen = openFaq === key;
                    return (
                        <div className={"faq-item" + (isOpen ? " faq-item-open" : "")} key={key}>
                            <button className="faq-question" onClick={() => setOpenFaq(isOpen ? null : key)}>
                                <span>{faq.question}</span>
                                {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                            {isOpen && (
                                <div className="faq-answer">{faq.answer}</div>
                            )}
                        </div>
                    );
                })}
            </div>
        </>
    );
}

export default function Faq() {
    const navigate = useNavigate();

    return (
        <PublicLayout activePage="FAQ">
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqSchema }} />
            <div className="upload-main">
                <FaqBody navigate={navigate} />
            </div>
        </PublicLayout>
    );
}
